<template>
  <v-app>
    <!-- PAGE-LOAD PROGRESS BAR
         Nuxt 2 rendered this bar automatically, which is why the Bridge tree
         configured it nowhere. Nuxt 4 ships no default indicator at all -- it has
         to be placed by hand -- so route changes lost their progress bar at the
         cutover. Same failure mode as the page transition in nuxt.config.ts: a
         Nuxt 2 default that is simply absent in Nuxt 4.
         Nothing here is inherited from Bridge, though it was checked: no file in
         the Bridge tree styled `.nuxt-progress` or set a `loading` config key, so
         Bridge ran Nuxt 2's stock defaults -- black, 2px, 5000ms. Every value
         below is a deliberate departure from those. (Nuxt 4 animates scaleX where
         Nuxt 2 animated width; for a solid bar that looks the same.)
         `duration` is 1500, not Nuxt 2's 5000, because it feeds an asymptotic
         curve -- progress = atan(elapsed/duration), which never reaches 100 on
         its own; page:loading:end is what snaps it there. On a ~1.1s nav here
         1500 measures 50% across at that moment; the same nav at 5000 works out
         to ~20% by the formula, i.e. the bar jumped four fifths of the way in
         one step, which reads as a glitch. Note the ramp does not start until
         after the throttle, so elapsed is a further 300ms less than the nav
         time -- raising the throttle lowers the progress reached, so the two
         values are coupled and should be retuned together.
         Colour and height depart from Nuxt 2's black/2px. The colour itself is
         theme-aware and lives in this file's <style> block as
         `--cynque-loading-bar` -- see the comment there for the two tokens and
         why the light one cannot simply be reused in dark mode.
         The alpha rides on the BACKGROUND colour, not the element's `opacity`:
         Nuxt drives `opacity` itself (1 while loading, 0 when idle, 0.4s fade),
         so setting that instead would pin the bar permanently visible.
         `throttle` is 300, up from Nuxt's 200 default (also Nuxt 2's). It is the
         quiet period before the bar is allowed to appear: a route change that
         completes inside it shows nothing at all, which is the point -- a bar
         that flashes on and straight off for an already-cached page is noise.
         Raising it also means the "no bar appeared" band is wider, so a fast nav
         legitimately showing nothing is expected, not a fault.
         `hide-delay` is 0 because the default 500 leaves the bar sitting at full
         width well after the page has painted. Measured on a nav here:
         page:loading:end at 1112ms, page content on screen at 1284ms, bar gone
         at 1960ms -- a 676ms tail. Nuxt fires page:loading:end from Suspense's
         onResolve, i.e. BEFORE the page's own mounted() work paints, so the
         default delay stacks on top of an indicator that is already done. 0
         starts the fade immediately; the 0.4s opacity fade is hard-coded in the
         component's inline style and is what remains. `reset-delay` stays at its
         400ms default -- it must not undercut that fade, or the bar would snap
         back to zero width while still visible. -->
    <NuxtLoadingIndicator
      color="var(--cynque-loading-bar)"
      :height="4"
      :duration="1500"
      :throttle="300"
      :hide-delay="0"
    />

    <!-- NAVIGATION DRAWER -->
    <v-navigation-drawer
      ref="navDrawer"
      v-model="drawer"
      width="271"
      :class="navDrawerClass"
      :color="navDrawerColor"
      app
    >
      <v-list shaped>
        <nuxt-link to="/Home">
          <v-img
            class="ml-5 mr-7 my-6"
            src="/WXTG_Logo_TransparentBackground2.png"
          ></v-img>
        </nuxt-link>
        <v-divider class="mb-2"></v-divider>
        <v-list-group value="filters">
          <template #activator="{ props }">
            <!-- pl-1 lives on the TITLE (as it did in V2's .v-list-item__title) so it
                 ADDS 4px to the item's 16px padding — text left 16 -> 20, lining the
                 word up with the icon glyphs below. On the item itself pl-1 would
                 OVERRIDE the 16px padding down to 4 instead of adding. -->
            <v-list-item v-bind="props">
              <template #title><span class="pl-1">Filters</span></template>
            </v-list-item>
          </template>
          <v-select
            v-model="viewAs"
            label="View As"
            class="mx-5"
            :items="groups"
            item-title="descr"
            item-value="id"
            :return-object="true"
            @focus="setUserGroups"
          />
        </v-list-group>
      </v-list>
      <v-divider></v-divider>
      <v-list v-model:opened="openedNavGroups" shaped open-strategy="single">
        <v-list-item
          key="home"
          to="/Home"
          router
          :active="false"
          :class="
            activeNavItemTo === '/Home'
              ? ['v-list-item--active', homeNavItemActiveClass]
              : undefined
          "
        >
          <template #prepend>
            <v-icon>mdi-home-outline</v-icon>
          </template>
          <v-list-item-title :style="homeListItemStyle">HOME</v-list-item-title>
        </v-list-item>
        <v-list-group
          v-for="item in listGroups"
          :key="item.title"
          :value="item.title"
          :class="navDrawerListGroupClass"
        >
          <!-- The highlighted (purple) group header is the OPEN one, which is what
               V2 did: its `<v-list-group :value="item.state" :active-class="...">`
               applied active-class via Toggleable, i.e. keyed on the group's
               EXPANDED state. The migration had keyed it on the current ROUTE's
               group instead, so expanding a different group left the highlight
               behind on the route's group until you navigated. `isOpen` comes from
               VListGroup's own activator slot, so it cannot drift from the real
               disclosure state the way an app-side mirror can. -->
          <template #activator="{ props, isOpen }">
            <v-list-item
              v-bind="props"
              :prepend-icon="item.icon"
              :class="{ [navDrawerListGroupActiveClass]: isOpen }"
            >
              <v-list-item-title class="groupTitle" v-text="item.title" />
            </v-list-item>
          </template>

          <v-divider class="mt-n2 mb-2" :inset="true" />

          <template v-for="(child, index) in item.items">
            <v-divider
              v-if="child.divider"
              :key="index"
              class="my-2"
              :inset="child.inset"
            />
            <v-list-item
              v-else
              :key="child.title"
              :to="encodeNavTo(child.to)"
              router
              :active="false"
              :class="{
                'v-list-item--active':
                  activeNavItemTo && activeNavItemTo === child.to,
              }"
            >
              <template #prepend>
                <v-icon>mdi-circle-small</v-icon>
              </template>
              <div v-if="child.childItems">
                <!-- Bridge's `offset-y`, `top` and `nudge-bottom="180"` are all
                       dead props in Vuetify 3 and were measurably inert, so they
                       are dropped rather than translated. What positions a 770px
                       list in a ~1000px viewport is the framework fitting it on
                       screen, and the two differ: V2 clamped to the viewport
                       bottom, V3 flips it above the activator (~134px higher, and
                       it tracks the activator as the nav scrolls).
                       `nav-flyout-bottom-pinned` (global block at the foot of this
                       file) pins it to the window bottom like V2 while keeping
                       V3's own max-height/scroll behaviour. -->
                <v-menu content-class="nav-flyout-bottom-pinned">
                  <template #activator="{ props }">
                    <v-list-item-title v-bind="props">{{
                      child.title
                    }}</v-list-item-title>
                  </template>
                  <!-- Bridge's height is 770. This list is pinned to the
                         window bottom (see `nav-flyout-bottom-pinned`), and its
                         height is slack rather than a fit: on Bridge the last
                         item ends 42px above the bottom edge. V3 stacks the same
                         10 items 32px tighter and starts them 8px higher inside
                         the list, so at 770 all 40px of that landed in the
                         bottom gap (82px against Bridge's 42). Take 40 off the
                         height instead: the tighter rows stay, and the last item
                         lands on Bridge's y=970..1026 with the same 42px below
                         it. Re-derive this number if the item list changes.

                         The Service Requests section (divider + subheader + one
                         item, just above Stages) measures 89px, divider to
                         divider, so the height is 730 + 89 = 819 while that
                         section shows, and 730 when isServiceRequestsEnabled()
                         hides it. Width 254, was 230: 'Service Request Types' is
                         160px of text in a 162px title box at 230, with no slack
                         for a wider font. -->
                  <v-list
                    width="254"
                    :height="selectListsFlyoutHeight"
                    :shaped="true"
                    :bg-color="selectListMenuColor"
                  >
                    <div
                      v-for="(childItem, index) in child.childItems"
                      :key="index"
                    >
                      <AppSubheader
                        v-if="childItem.isSubheader"
                        class="mb-n3"
                        >{{ childItem.title }}</AppSubheader
                      >
                      <v-divider v-else-if="childItem.isDivider" class="mt-3" />
                      <v-list-item
                        v-else
                        class="mb-n2"
                        :to="encodeNavTo(childItem.to)"
                        router
                        :active="false"
                        :class="{
                          'v-list-item--active':
                            activeNavItemTo && activeNavItemTo === childItem.to,
                        }"
                      >
                        <template #prepend>
                          <v-icon>mdi-circle-small</v-icon>
                        </template>
                        <v-list-item-title>{{
                          childItem.title
                        }}</v-list-item-title>
                      </v-list-item>
                    </div>
                  </v-list>
                </v-menu>
              </div>
              <div v-else>
                <v-list-item-title>{{ child.title }}</v-list-item-title>
              </div>
            </v-list-item>
          </template>
        </v-list-group>
        <!-- <v-divider class="mt-2"></v-divider>
        <v-switch
          v-model="$vuetify.theme.dark"
          class="ml-4"
          inset
          label="Dark Mode"
          @click="didToggleDarkMode"
        ></v-switch> -->
      </v-list>
    </v-navigation-drawer>

    <!-- APP TOP NAV BAR -->
    <v-app-bar fixed app :color="appBarColor">
      <v-app-bar-nav-icon @click.stop="toggleDrawer" />
      <v-toolbar-title :class="titleClass">
        <OutlinedAvatar
          v-if="titleAvatar?.type === 'account'"
          class="title-bar-avatar"
          :color="getAvatarHexColor(titleAvatar.type)"
          :rounded="getAvatarRounded(titleAvatar.type)"
          size="38"
        >
          <span
            class="font-weight-bold text-body-1"
            :class="getAvatarTextColor(titleAvatar.type)"
          >
            {{ titleAvatar.descr }}
          </span>
        </OutlinedAvatar>
        <v-avatar
          v-else-if="titleAvatar"
          class="mb-1 title-bar-avatar"
          size="38"
          :color="getAvatarColor(titleAvatar.type)"
          :rounded="getAvatarRounded(titleAvatar.type)"
        >
          <span class="font-weight-bold text-white text-body-1">
            {{ titleAvatar.descr }}
          </span>
        </v-avatar>
        <nuxt-link
          v-if="titlePrefix"
          :class="titlePrefixLinkClass"
          :to="encodeNavTo(titlePrefix.link)"
        >
          {{ titlePrefix.prefix }}
        </nuxt-link>
        {{ title }}
      </v-toolbar-title>
      <v-spacer />

      <!-- User Avatar & Drop-Down Menu -->
      <v-menu open-on-hover offset-y>
        <template #activator="{ props }">
          <v-btn icon class="px-10" v-bind="props">
            <v-avatar color="secondary" size="48">
              <span class="text-white text-h5">{{ loggedInUserInitials }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <!-- width 204 so the centered 156px buttons land on a clean 24px left
             edge; the icon rows (pl-6) and the reversed switch labels (ml-6) sit
             at that same 24px, so every left edge in the menu aligns. -->
        <v-list width="204">
          <v-list-item class="pl-6" @click="logout">
            <template #prepend>
              <v-icon>mdi-logout</v-icon>
            </template>
            <v-list-item-title>Log Out</v-list-item-title>
          </v-list-item>
          <v-list-item class="pl-6" @click="editMySettings">
            <template #prepend>
              <v-icon>mdi-account-cog-outline</v-icon>
            </template>
            <v-list-item-title>My Settings</v-list-item-title>
          </v-list-item>
          <v-list-item class="pl-6" @click="showPasskeysDialog = true">
            <template #prepend>
              <v-icon>mdi-fingerprint</v-icon>
            </template>
            <v-list-item-title>Passkeys</v-list-item-title>
          </v-list-item>
          <v-divider class="mt-2" />

          <v-switch
            v-model="darkMode"
            class="ml-6 mr-6 mt-4 v-input--reverse"
            inset
            hide-details
            color="primary"
            label="Dark Mode"
          />
          <!-- Double-line separator. `avatar-double-divider` (global.css) matches
               the overview header-card menus' double line EXACTLY: rgb(220,220,220)
               lines 6px apart. NB: NOT `v-divider--thick` -- that class's colour
               comes from a *MSServicesMenu component <style> that only loads when
               those components are on the page, so on other pages the thick
               divider fell back to near-black (0.87). -->
          <v-divider class="mt-2 avatar-double-divider" />
          <v-divider class="avatar-double-divider" />
          <v-switch
            v-model="doPersistMockData"
            class="ml-6 mr-6 mt-4 v-input--reverse"
            inset
            hide-details
            color="primary"
            label="Persist Mock"
            @click="didToggleDoPersistMockData"
          />
          <v-btn
            variant="outlined"
            :disabled="!doesHaveEntityDataInLocalStorage"
            class="d-block mx-auto mt-2 mb-5"
            width="156"
            color="#777"
            @click="clearPersistedDataInLocalStorage"
            >Clear Persisted</v-btn
          >
          <v-btn
            variant="outlined"
            :disabled="!doesHaveEntityDataInLocalStorage"
            class="d-block mx-auto mb-5"
            width="156"
            color="#777"
            @click="clearPersistedDataInLocalStorageAndRefreshBrowser"
            >Clear+Refresh</v-btn
          >
          <v-btn
            variant="outlined"
            class="d-block mx-auto mb-5"
            width="156"
            color="#777"
            @click="
              clearPersistedDataAndVuexDataInLocalStorageAndRefreshBrowser
            "
            >Start Over</v-btn
          >
        </v-list>
      </v-menu>
    </v-app-bar>

    <Snackbar />

    <!-- BODY -->
    <v-main>
      <NotificationContainer />
      <v-container class="py-8 px-6" fluid>
        <slot />
      </v-container>
    </v-main>

    <!-- FOOTER -->
    <Footer />

    <!-- DIALOGS -->
    <EntityDialog :show-entity-dialog="showEntityDialog" />
    <EntityDialog
      :show-entity-dialog="showSubentityDialog"
      :is-subentity-dialog="true"
    />
    <ModalDialog :show-modal-dialog="showModalDialog" />
    <PasskeyNudgeDialog />
    <PasskeysDialog v-model="showPasskeysDialog" />
  </v-app>
</template>

<script lang="ts">
/* eslint-disable no-console */
import { defineComponent } from 'vue'
import { isDarkTheme, setDarkTheme } from '~/cynqueModules/nuxtAccessors'
import AvatarMixin from '~/cynqueModules/mixins/AvatarMixin'
import type { IGroup } from '~/types/group/models'
import { getGroups } from '~/services/groups'
import { getUser, logoutUser, isAuthenticated } from '~/services/users'
import { simplePluralize } from '~/cynqueModules/utils/pluralize'
import {
  hasEntityDataInLocalForage,
  clearEntityDataInLocalForage,
} from '~/api/msw/modules/mockDataStorageManager'
import { clearVuexDataInLocalStorage } from '~/cynqueModules/clearVuexDataInLocalStorage'
import { setShowEntityDialog } from '~/cynqueModules/setShowEntityDialog'
import { handleAsyncDataErrors } from '~/cynqueModules/handleAsyncDataErrors'
import { getNuxtContext } from '~/cynqueModules/nuxtRuntime'
import {
  onWindowFocus,
  removeWindowOnFocusListener,
} from '~/cynqueModules/onWindowFocus'
import {
  createNavDrawerResizeObserver,
  addResizeObserverWhenDesiredNodeAvailable,
  removeNavDrawerResizeObserver,
} from '~/cynqueModules/navDrawerOverflowXResizeObserver'
import { useDefaultLayoutVarsStore } from '~/stores/defaultLayoutVars'
import { useEntityDialogStore } from '~/stores/entityDialog'
import { useLoggedInUserStore } from '~/stores/loggedInUser'
import { useModalDialogStore } from '~/stores/modalDialog'
import { usePageParamsStore } from '~/stores/pageParams'
import { useSettingsStore } from '~/stores/settings'
import { useUserStore } from '~/stores/user'
import { isServiceRequestsEnabled } from '~/cynqueModules/isServiceRequestsEnabled'

const mn = '[layouts/default.vue]'

export default defineComponent({
  // Auth is enforced by middleware/authenticated.global.ts (Nuxt 4 dropped
  // layout-declared middleware); the old setContextReferenceInUserServicesModule
  // middleware is gone (services reach the store via cynqueModules/nuxtRuntime).
  mixins: [AvatarMixin],
  data() {
    return {
      doesHaveEntityDataInLocalStorage: false,
      // Honestly optional: mounted() returns early when there is no logged-in
      // user, so this stays undefined. The old `as unknown as ResizeObserver`
      // cast asserted that away, which is why beforeUnmount()'s teardown
      // throwing on it was invisible to the type-checker.
      navDrawerOverflowXResizeObserver: undefined as ResizeObserver | undefined,
      navDrawerContentElement: undefined as unknown as Element | undefined,
      navDrawerIsOverflowXed: false,
      // See the Select Lists v-list: +89px for the Service Requests section.
      selectListsFlyoutHeight: isServiceRequestsEnabled() ? 819 : 730,
      listGroups: [
        {
          icon: 'mdi-speedometer',
          title: 'DASHBOARDS',
          permissionKey: 'dashboards',
          state: false,
          items: [
            {
              title: 'Default',
              to: '/Dashboard',
            },
          ],
        },
        {
          icon: 'mdi-lightbulb-variant-outline',
          title: 'OPERATIONS',
          permissionKey: 'operations',
          state: false,
          items: [
            {
              title: 'Accounts',
              to: '/Accounts',
            },
            {
              title: 'Companies',
              to: '/Companies',
            },
            {
              title: 'Opportunities',
              to: '/Opportunities',
            },
            {
              title: 'Orders',
              to: '/Orders',
            },
            { divider: true, inset: true },
            // ALWAYS listed, even while Service Requests are off: the page shows
            // its placeholder until they go live (pages/Service Requests/index.vue).
            {
              title: 'Service Requests',
              to: '/Service Requests',
            },
            {
              title: 'Cases',
              to: '/Cases',
            },
            { divider: true, inset: true },
            {
              title: 'Projects',
              to: '/Projects',
            },
            {
              title: 'Contacts',
              to: '/Contacts',
            },
            { divider: true, inset: true },
          ],
        },
        {
          icon: 'mdi-cash',
          title: 'ACCOUNTING',
          permissionKey: 'accounting',
          state: false,
          items: [
            {
              title: 'Receivables',
              to: '/Receivables',
            },
            {
              title: 'Payables',
              to: '/Payables',
            },
            { divider: true, inset: true },
          ],
        },
        {
          icon: 'mdi-account-cog-outline',
          title: 'ADMIN',
          permissionKey: 'admin',
          state: false,
          items: [
            {
              title: 'Users',
              to: '/Users',
            },
            {
              title: 'Groups',
              to: '/Groups',
            },
            { divider: true, inset: true },
            {
              title: 'Products',
              to: '/Product Categories',
            },
            {
              title: 'Suppliers',
              to: '/Suppliers',
            },
            {
              title: 'TSDs',
              to: '/TSDs',
            },
            {
              title: 'Team Members',
              to: '/Team Members',
            },
            {
              title: 'Persons',
              to: '/Persons',
            },
            { divider: true, inset: true },
            {
              title: 'Field Sets',
              to: '/Field Sets',
            },
            {
              title: 'Select Lists',
              to: '',
              childItems: [
                {
                  isSubheader: true,
                  title: 'Cases',
                },
                {
                  title: 'Case Types',
                  to: '/Case Types',
                },
                {
                  isDivider: true,
                },
                {
                  isSubheader: true,
                  title: 'Company',
                },
                {
                  title: 'Industries',
                  to: '/Industries',
                },
                {
                  title: 'Location Types',
                  to: '/Location Types',
                },
                {
                  isDivider: true,
                },
                {
                  isSubheader: true,
                  title: 'Contact Roles',
                },
                {
                  title: 'Company',
                  to: '/Company Contact Roles',
                },
                {
                  title: 'Supplier',
                  to: '/Supplier Contact Roles',
                },
                {
                  title: 'TSD',
                  to: '/TSD Contact Roles',
                },
                {
                  isDivider: true,
                },
                {
                  isSubheader: true,
                  title: 'Lead Source Types',
                },
                {
                  title: 'Events',
                  to: '/Lead Source Events',
                },
                {
                  title: 'Campaigns',
                  to: '/Lead Source Campaigns',
                },
                {
                  title: 'Contacts',
                  to: '/Lead Source Contacts',
                },
                ...(isServiceRequestsEnabled()
                  ? [
                      {
                        isDivider: true,
                      },
                      {
                        isSubheader: true,
                        title: 'Service Requests',
                      },
                      {
                        title: 'Service Request Types',
                        to: '/Service Request Types',
                      },
                    ]
                  : []),
                {
                  isDivider: true,
                },
                {
                  isSubheader: true,
                  title: 'Stages',
                },
                {
                  title: 'Opportunity Stages',
                  to: '/Opportunity Stages',
                },
                // {
                //   title: 'Internal Sources',
                //   to: '/Lead Source Internal Sources',
                // },
                // {
                //   title: 'External Sources',
                //   to: '/Lead Source External Sources',
                // },
              ],
            },
            {
              title: 'Settings',
              to: '/Settings',
            },
          ],
        },
      ],

      miniVariant: false,
      showPasskeysDialog: false,
      viewAs: { id: 'XXG0002', descr: 'WXTG' },
      groups: [] as unknown as IGroup[],
    }
  },
  computed: {
    // Two-way model for the "Dark Mode" switch. `get` reflects the live Vuetify
    // theme (reactive via isDarkTheme); `set` flips the theme immediately and
    // persists the choice to the store, which vuex-persist restores on the next
    // load via setVuetifyTheme().
    darkMode: {
      get(): boolean {
        return isDarkTheme()
      },
      set(value: boolean) {
        setDarkTheme(value)
        useSettingsStore().setUseDarkMode(value)
      },
    },
    // Vuetify 3 moves list-group open state onto the parent <v-list v-model:opened>
    // (an array of the open groups' `value` identifiers) instead of a boolean per
    // group. Bridge it to the existing per-group `state` flag, which the route-driven
    // setNavDrawerGroupStateValues() still owns: get exposes the open groups' titles;
    // set writes each group's `state` back when the user toggles a disclosure.
    openedNavGroups: {
      get(): string[] {
        return this.listGroups
          .filter((group) => group.state)
          .map((group) => group.title)
      },
      set(openTitles: string[]) {
        this.listGroups.forEach((group) => {
          group.state = openTitles.includes(group.title)
        })
      },
    },
    navWidth() {
      const el2 = document.getElementsByClassName(
        'v-navigation-drawer__content'
      )
      if (!el2) {
        return undefined
      }
      if (!el2[0]?.clientWidth) {
        return undefined
      }
      const width = el2[0].clientWidth
      return width
    },
    doPersistMockData: {
      get() {
        return useSettingsStore().doPersistMockData
      },
      set(value) {
        useSettingsStore().setDoPersistMockData(value)
      },
    },
    loggedInUserInitials() {
      const loggedInUser = useLoggedInUserStore().getLoggedInUser
      return loggedInUser ? loggedInUser.person?.name?.initials ?? '' : '--'
    },
    routeName(): string | undefined | null {
      console.debug('routeName changed. From inside computed.')
      return this.$route.name
    },
    titleAvatar() {
      return useDefaultLayoutVarsStore().getMenuBarAvatar
    },
    titlePrefix() {
      return useDefaultLayoutVarsStore().getMenuBarPrefix
    },
    title() {
      return useDefaultLayoutVarsStore().getMenuBarTitle
    },
    titleClass() {
      // V2 -> V3: `headline` -> `text-h5` (both 24px/400), and the theme-color classes
      // `text-secondary-darken-1 / --lighten-5` -> `text-secondary-darken-1` /
      // `-lighten-5` (theme-color variations are enabled in nuxt.config.ts; the values
      // are identical to V2's).
      //
      // `pl-5` (20px) replaces a Vuetify 2 rule that has no V3 equivalent:
      //   .v-toolbar__content > .v-btn.v-btn--icon:first-child + .v-toolbar__title
      //     { padding-left: 20px }
      // i.e. V2 indented a toolbar title that follows the nav-icon button, which is
      // exactly this bar. V3's .v-toolbar-title has no such padding, so the title
      // text sat 20px left of Bridge's (measured: text x 339 vs 359 on /Companies —
      // the ELEMENT box is at 339 in both, only the text inside it moved). Applied
      // to the title element, where V2 applied it, rather than as a global rule:
      // the app's only other <v-toolbar-title> (NotificationBar) follows a
      // <v-spacer />, not an icon button, so V2 never padded it and it must not
      // gain padding now.
      return isDarkTheme()
        ? 'page-title-bar-title ml-4 pl-5 text-h5 text-secondary-lighten-5'
        : 'page-title-bar-title ml-4 pl-5 text-h5 text-secondary-darken-1'
    },
    nuxtLinkClass() {
      return isDarkTheme()
        ? 'text-secondary-lighten-5'
        : 'text-secondary-darken-1'
    },
    titlePrefixLinkClass() {
      // Single string (not an array) so vue-tsc sees one :class expression.
      // Lets a prefix opt into extra classes (e.g. 'text-decoration-none' for
      // the product-hierarchy back-arrows) without underlining text prefixes.
      const prefix = this.titlePrefix as { class?: string } | null
      const extra = prefix?.class ? ` ${prefix.class}` : ''
      return `${this.nuxtLinkClass}${extra}`
    },
    drawer: {
      get() {
        return useDefaultLayoutVarsStore().getDrawer
      },
      set(val) {
        useDefaultLayoutVarsStore().setDrawer(val)
      },
    },
    visibleListGroups() {
      const user = useLoggedInUserStore().getLoggedInUser
      const permissions = user?.permissions
      if (!permissions) {
        return []
      }
      if (permissions.isSuperAdmin) {
        return this.listGroups
      }
      return this.listGroups.filter((group) => {
        const perms = permissions[group.permissionKey]
        return perms && perms.some((p) => p.id === 1)
      })
    },
    navDrawerClass() {
      // Background moved to navDrawerColor (the `color` prop) — `bg-grey-lighten-3`
      // is #EEEEEE, which light mode still gets, but dark mode needs #363636 and
      // no Vuetify grey utility lands on that value.
      return isDarkTheme() ? 'navDrawerItem darkScrollBar' : 'navDrawerItem'
    },
    // Vuetify 2 lightened dark surfaces by ELEVATION (Material dark elevation
    // overlays): cards sat at #1E1E1E, the app bar at #272727 and the nav drawer
    // at #363636. Vuetify 3 dropped that system and paints every surface flat from
    // the one `surface` token, so the app-wide `surface: '#1E1E1E'` in
    // nuxt.config.ts matches Bridge's CARDS but leaves these two chrome elements
    // too dark. Name Bridge's measured values here.
    navDrawerColor() {
      return isDarkTheme() ? '#363636' : '#EEEEEE'
    },
    // Light mode differed too, independently of the dark work: Bridge painted the
    // app bar #F5F5F5 while V3 fell through to the light surface #FFFFFF.
    appBarColor() {
      return isDarkTheme() ? '#272727' : '#F5F5F5'
    },
    homeListItemStyle() {
      return this.navDrawerIsOverflowXed
        ? { 'font-weight': '700', 'font-size': '11pt' }
        : { 'font-weight': '600' }
    },
    navDrawerListGroupClass() {
      return this.navDrawerIsOverflowXed ? 'navDrawerOverflowXed' : undefined
    },
    homeNavItemActiveClass() {
      // HOME's active shade. Vuetify's `text-*` utilities carry `!important`, so
      // any one bound here BEATS the `.v-list-item--active` CSS rules at the foot
      // of this file regardless of specificity -- a hazard, because it silently
      // pins HOME to a different shade from every other active item.
      //
      // DARK returns undefined ON PURPOSE: with no utility in the way, the dark
      // CSS rule governs HOME like everything else, so the shade lives in exactly
      // one place. That is also required -- dark's #8A8A8A sits deliberately
      // BETWEEN two Vuetify grey steps, so no utility could express it.
      //
      // LIGHT keeps `text-grey` (#9E9E9E), which is what light-mode HOME has
      // always rendered. Note that means the light CSS rule's rgb(120,120,120)
      // has never applied to HOME -- a pre-existing inconsistency, left alone
      // because this round of work is dark-mode only.
      return isDarkTheme() ? undefined : 'text-grey'
    },
    navDrawerListGroupActiveClass() {
      // Active-category (open group header) text shade. V2 used
      // `primary--text text--darken-2` (light) / `secondary--text
      // text--lighten-3` (dark); the migration had dropped the shade modifier
      // (base primary/secondary). Theme variations are enabled (nuxt.config.ts),
      // which is what makes these tokens resolve at all.
      //
      // NEITHER is at V2's value now, and both departures are deliberate: light
      // is darken-1, one step LIGHTER than V2's darken-2. Dark has left the token
      // system altogether -- see below. Don't "restore" either on parity grounds.
      //
      // ⚠ DARK RETURNS A CUSTOM CLASS, NOT A VUETIFY UTILITY. Its shade is a HALF
      // step: the variations are a CIELAB lightness shift of N*10 off the base
      // (nuxt.config.ts), and the value wanted here is base + 15 -- between
      // lighten-1 and lighten-2, so no `text-secondary-lighten-N` can express it.
      // `navDrawerListGroupActive` carries it instead (rule at the foot of this
      // file, where the derivation and the exact hex live).
      //
      // It is tuned against the INACTIVE group headers beside it, which carry no
      // colour of their own and so inherit the nav body text (#9E9E9E, 4.51:1 on
      // the #363636 drawer). The half step is 5.38:1 -- +0.87 over them, midway
      // between lighten-2's +1.75 and lighten-1's +0.08. V2's lighten-3 was
      // +1.75... at 8.3:1 absolute; it was calibrated against WHITE rows and
      // shouted once those rows were dimmed to grey.
      return isDarkTheme()
        ? 'navDrawerListGroupActive'
        : 'text-primary-darken-1'
    },
    activeNavItemTo(): string {
      // `to` of the nav item to highlight, using V2's full path-PREFIX rule:
      // exact match OR any route nested beneath it. This covers the cases V3's
      // router-link-active misses -- (a) silo OVERVIEWS (Vue Router 4 marks a link
      // active only on its own route or an ANCESTOR record, and the overview is a
      // SIBLING record), and (b) space-bearing pages (V3 renders `to="/Service
      // Requests"` as an unencoded href that never matches `/Service%20Requests`).
      //
      // This is the SINGLE SOURCE of the nav highlight: each nav <v-list-item>
      // carries a static `:active="false"`, which disables Vuetify's own
      // router-active, so `v-list-item--active` is added ONLY by our class binding
      // driven by this computed. That was the fix for the "from item flashes on a
      // nav click": V2 had one highlight source (vue-router 3's prefix-matching
      // active-class), but the migration left BOTH systems running -- Vuetify's
      // router-active AND this computed, toggling the same class over one crossfaded
      // overlay -- and their sub-frame timing difference blipped the outgoing item.
      // One source removes the handoff entirely. Because we're now the only source,
      // claiming the EXACT match here is REQUIRED (not just descendant) -- it's what
      // lights an item on its own page.
      // Decode so space-bearing paths compare; the trailing '/' keeps `/Cases`
      // from swallowing `/Case Types`.
      //
      // A COMPUTED (not a watcher-set data prop) so it derives from `$route` in the
      // same reactive flush as the route change. NOTE the `:active="false"` must be
      // STATIC: a DYNAMIC `:active` binding (toggling with the route) re-runs
      // VListItem's genOverlays()/scrollIntoView mid-navigation and broke row + nav
      // clicks (two-click / parentNode error) -- a constant false never toggles, so
      // it's safe and just switches Vuetify's active off for good.
      const path = decodeURIComponent(this.$route.path)
      const claims = (to?: string) =>
        !!to && (path === to || path.startsWith(`${to}/`))
      if (claims('/Home')) {
        return '/Home'
      }
      for (const group of this.listGroups) {
        for (const item of group.items) {
          if (claims(item.to)) {
            return item.to
          }
          if (item.childItems) {
            for (const childItem of item.childItems) {
              if (claims(childItem.to)) {
                return childItem.to
              }
            }
          }
        }
      }
      return ''
    },
    selectListMenuColor() {
      // Dark mode used to fall through to the framework's default surface, and
      // the two frameworks disagree: Vuetify 2 painted #1E1E1E, Vuetify 3 uses
      // #212121 (vuetify/lib/composables/theme.js). Measured on this flyout as
      // rgb(30,30,30) vs rgb(33,33,33). Name Bridge's value explicitly instead of
      // inheriting V3's. NOTE: the same 3-step difference applies to EVERY dark
      // surface in the app (cards, dialogs, other menus) — fixing it globally
      // means setting `surface` in the V3 dark theme, which is a far wider change
      // than this one menu and has not been done.
      return isDarkTheme() ? '#1E1E1E' : 'rgba(252,250,250)'
    },
    showEntityDialog() {
      return useEntityDialogStore().showEntityDialog === true
    },
    showSubentityDialog() {
      return useEntityDialogStore().showSubentityDialog === true
    },
    showModalDialog() {
      return useModalDialogStore().showModalDialog === true
    },
    theme() {
      if (process.server) {
        return
      }
      const bodyElement = document.getElementsByTagName('body')[0]
      // Line below does seem to work despite classList supposedly being read only
      // @ts-ignore
      bodyElement.classList = isDarkTheme() ? 'darkScrollbar' : ''
      return isDarkTheme() ? 'dark' : 'light'
    },
  },
  watch: {
    theme() {
      console.debug('theme changed')
    },
    navDrawerIsOverflowXed() {
      console.debug('nav drawer overflowX changed')
    },
    $route(to, from) {
      const fn = `${mn} watch ($route) |`
      console.debug(
        `${fn} route changed, from ${from.fullPath}, to ${to.fullPath}`
      )
      this.setNavDrawerGroupStateValues(to)
      usePageParamsStore().setLastRoute(to.fullPath)
    },
  },
  async created() {
    const fn = `${mn} created |`
    console.debug(`${fn} LIFECYCLE HOOK FIRED: CREATED`)
    const loggedInUser = useLoggedInUserStore().getLoggedInUser
    if (!loggedInUser) {
      console.debug(`${fn} No loggedInUser. Aborting created hook.`)
      return
    }
    console.debug(`${fn} route: ${this.$route.path}`)
    if (!this.$route.name) {
      console.debug(`${fn} route has no name.`)
      return
    }
    this.setNavDrawerGroupStateValues(this.$route)
    this.setVuetifyTheme()
    this.setUserGroups()
    onWindowFocus(this.windowFocusDidFire)
  },
  async mounted() {
    const fn = `${mn} mounted |`
    console.debug(`${fn} LIFECYCLE HOOK FIRED: MOUNTED`)
    const loggedInUser = useLoggedInUserStore().getLoggedInUser
    if (!loggedInUser) {
      console.debug(`${fn} No loggedInUser. Aborting mounted hook.`)
      return
    }
    this.doesHaveEntityDataInLocalStorage = await hasEntityDataInLocalForage()
    window.addEventListener(
      'entity-data-in-localforage-changed',
      this.handleEntityDataInLocalForageChanged
    )

    this.navDrawerOverflowXResizeObserver = createNavDrawerResizeObserver({
      callbackIsOverflowXed: this.handleNavDrawerIsOverflowXed,
      callbackIsNoLongerOverflowXed: this.handleNavDrawerIsNoLongerOverflowXed,
      // The observer's decision is hysteretic, so it has to know which state it
      // is leaving. Read live rather than mirrored — a mirror would drift from
      // the class the template is actually rendering.
      getIsCurrentlyOverflowXed: () => this.navDrawerIsOverflowXed,
    })
    this.navDrawerContentElement = addResizeObserverWhenDesiredNodeAvailable({
      observer: this.navDrawerOverflowXResizeObserver,
    })

    // ----------------------------------------------------------------
    // Old code from ccWrangler; keeping until we have real login
    // and delivery of user info in place. JR.
    //
    // const hasALogin = StorageService.getHasALogin()
    // if (!hasALogin) {
    //   console.debug(`${fn} No hasALogin in localStorage`)
    //   console.debug(`${fn} Pushing to homepage`)
    //   this.$router.push('/')
    //   return
    // }

    // console.debug(`${fn} Has hasALogin in localStorage`)
    // const user = StorageService.getUser()
    // if (!user) {
    //   console.debug(`${fn} No user in localStorage`)
    //   this.$store.dispatch('user/setHasALogin').then(() => {
    //     console.debug(`${fn} Pushing 'Login'`)
    //     this.$router.push({ name: 'Login' })
    //   })
    //   // eslint-disable-next-line no-unreachable
    // } else {
    //   console.debug(`${fn} Has user in localStorage`)
    //   this.$store
    //     .dispatch('user/set', user)
    //     .then(() => {
    //       this.$store.dispatch('user/setHasALogin')
    //     })
    //     .then(() => {
    //       console.debug(`${fn} Pushing 'Dashboard'`)
    //       // this.$router.push({ name: 'Dashboard' })
    //     })
    // }
    // ----------------------------------------------------------------
  },
  // Vue 3 renamed `beforeDestroy` to `beforeUnmount`. Under the old name this
  // hook was silently never called, so none of the teardown below ran.
  beforeUnmount() {
    const fn = `${mn} beforeUnmount |`
    console.debug(`${fn} LIFECYCLE HOOK FIRED: BEFORE UNMOUNT`)
    window.removeEventListener(
      'entity-data-in-localforage-changed',
      this.handleEntityDataInLocalForageChanged
    )
    removeWindowOnFocusListener()
    removeNavDrawerResizeObserver({
      resizeObserver: this.navDrawerOverflowXResizeObserver,
      el: this.navDrawerContentElement,
    })
  },
  methods: {
    // %20-encode literal spaces in a nav `:to`. Nuxt registers space-bearing
    // pages under their ENCODED path (`/Field%20Sets`), so Vue Router 4 resolves
    // a raw `to="/Field Sets"` to NO match and logs `[VUE_ROUTER_R0004] No match
    // found` on every render of the nav (Vuetify's VListItem resolves each `:to`
    // to build its href). Feeding the encoded path resolves cleanly and also
    // navigates directly (no reliance on the encodeSpacePaths redirect). The
    // active-highlight comparison is unaffected: activeNavItemTo() decodes
    // `$route.path` before comparing to the raw `child.to`. No-op for paths
    // without spaces.
    encodeNavTo(path?: string): string | undefined {
      return path ? path.replace(/ /g, '%20') : path
    },
    testClick() {
      console.debug('testClick')
    },
    // Must be a `methods` entry, not an inline arrow at the call site: Vue binds
    // each method ONCE, so `this.handleEntityDataInLocalForageChanged` is the same
    // function object every time it's read, and addEventListener /
    // removeEventListener therefore match. The previous code passed a freshly
    // constructed arrow to each, so the removal could never match the
    // registration and the listener outlived the layout.
    async handleEntityDataInLocalForageChanged() {
      const fn = `${mn} handleEntityDataInLocalForageChanged |`
      console.debug(`${fn} EVENT FIRED`)
      this.doesHaveEntityDataInLocalStorage = await hasEntityDataInLocalForage()
    },
    async windowFocusDidFire() {
      const fn = `${mn} windowFocusDidFire |`
      console.debug(`${fn} EVENT FIRED: WINDOW FOCUS`)
      try {
        await isAuthenticated()
        console.debug(`${fn} user is authenticated`)
      } catch (error) {
        handleAsyncDataErrors({
          callingMn: fn,
          error,
          context: getNuxtContext(),
        })
      }
    },
    async clearPersistedDataInLocalStorage() {
      console.debug(
        `${mn} clearPersistedDataInLocalStorage | BUTTON CLICKED: CLEAR PERSISTED DATA`
      )
      const didClear = await clearEntityDataInLocalForage()
      const snackMessage = didClear
        ? `Persisted mock data has been deleted from localForage`
        : `There is no persisted mock data to delete`
      // @ts-ignore
      this.$setSnack({
        callingMn: mn,
        snack: {
          message: snackMessage,
          timeout: 4000,
        },
      })
    },
    async clearPersistedDataInLocalStorageAndRefreshBrowser() {
      console.debug(
        `${mn} clearPersistedDataInLocalStorageAndRefreshBrowser | BUTTON CLICKED: CLEAR+REFRESH DATA`
      )
      await clearEntityDataInLocalForage()
      location.reload()
    },
    async clearPersistedDataAndVuexDataInLocalStorageAndRefreshBrowser() {
      console.debug(
        `${mn} clearPersistedDataAndVuexDataInLocalStorageAndRefreshBrowser | BUTTON CLICKED: START OVER`
      )
      await clearEntityDataInLocalForage()
      await this.logout()
      clearVuexDataInLocalStorage()
      location.reload()
    },
    handleNavDrawerIsOverflowXed() {
      const fn = `${mn} handleNavDrawerIsOverflowXed |`
      console.debug(`${fn} fired`)
      this.navDrawerIsOverflowXed = true
    },
    handleNavDrawerIsNoLongerOverflowXed() {
      const fn = `${mn} handleNavDrawerIsNoLongerOverflowXed |`
      console.debug(`${fn} fired`)
      this.navDrawerIsOverflowXed = false
    },
    async logout() {
      console.debug(`${mn} logout | BUTTON CLICKED: LOG OUT`)
      await logoutUser()
      useUserStore().logout() // Not yet doing anything relevant JR
    },
    editMySettings() {
      const fn = `${mn} editMySettings |`
      console.debug(`${fn} BUTTON CLICKED: EDIT MY SETTINGS`)
      const entityData = useLoggedInUserStore().getLoggedInUser
      setShowEntityDialog({
        callingMn: fn,
        entityName: 'user',
        entityData,
        inputFieldsEntityNameOverride: 'mySettings',
        titleOverride: 'My Settings',
        desiredAction: 'EDIT',
        afterRequestCallback: async () => {
          try {
            const user = await getUser(entityData.id)
            console.debug(`${fn} user:`, user)
            console.debug(
              `${fn} setting loggedInUser in store to user, ${user.id}`
            )
            await useLoggedInUserStore().setLoggedInUser(user)
          } catch (e) {
            handleAsyncDataErrors({
              callingMn: fn,
              error: e,
              context: getNuxtContext(),
            })
          }
        },
      })
    },
    toggleDrawer() {
      console.debug(
        `${mn} toggleDrawer | ICON CLICKED. Setting drawer ${!this.drawer}`
      )
      useDefaultLayoutVarsStore().setDrawer(!this.drawer)
    },
    didToggleDoPersistMockData() {
      console.debug(
        `${mn} didToggleDoPersistMockData | SWITCH CLICKED. Persist ${this.doPersistMockData}`
      )
      // @ts-ignore
      this.$setSnack({
        callingMn: mn,
        snack: {
          message: `Persist Mock Data is now ${
            this.doPersistMockData ? 'on' : 'off'
          }.`,
          timeout: 1850,
        },
      })
    },
    setVuetifyTheme() {
      setDarkTheme(useSettingsStore().useDarkMode)
    },
    setNavDrawerGroupStateValues(route) {
      const fn = `${mn} setNavDrawerGroupStateValues |`
      console.debug(`${fn}`)
      if (!route.name) {
        throw new Error(
          `${fn} route.name is falsey. Most likely route is not defined in '/server/api'`
        )
      }
      const currentPath =
        '/' +
        route.name
          .replace(/-id$/, '')
          .replaceAll('-', '/')
          .replaceAll(' ', '')
          .replace(/((?<!\/)[A-Z])/g, ' $1')
          .trim()
      console.debug(
        `${fn} route.name: ${route.name},` +
          ` currentPath: ${currentPath},` +
          ` unprocessedPath: ${route.path}`
      )
      this.listGroups.forEach((group, index) => {
        console.debug(
          `${fn} BEFORE - group: ${group.title}, state: ${group.state}`
        )
      })

      // Accordion: only one group open at a time. Reset all first so the route match
      // below leaves exactly one open, keeping the route-driven state consistent with
      // the v-list's open-strategy="single" (which enforces the same on user clicks).
      this.listGroups.forEach((group) => {
        group.state = false
      })

      const groupExceptions = [
        { group: '/Home', includes: '/Home/Overview/Activities' },
        { group: '/Home', includes: '/Home/Overview/Operations' },
        { group: '/Home', includes: '/Home/Overview/Sales' },

        { group: '/Companies', includes: '/Locations' },
        { group: '/Companies', includes: '/Companies/Overview/Activities' },
        { group: '/Companies', includes: '/Companies/Overview/Operations' },
        { group: '/Companies', includes: '/Companies/Overview/Sales' },
        {
          group: '/Opportunities',
          includes: '/Opportunities/Overview/Activities',
        },
        {
          group: '/Opportunities',
          includes: '/Opportunities/Overview/Operations',
        },
        { group: '/Opportunities', includes: '/Opportunities/Overview/Sales' },

        { group: '/Product Categories', includes: '/Product Subcategories' },
        { group: '/Product Categories', includes: '/Product Groups' },
        { group: '/Product Categories', includes: '/Products' },

        { group: '/Suppliers', includes: '/Suppliers/Overview/Activities' },
        { group: '/Suppliers', includes: '/Suppliers/Overview/Sales' },

        { group: '/TSDs', includes: '/T S Ds' },
      ]
      const itemExceptions = [
        { childItem: '/TSD Contact Roles', includes: '/T S D Contact Roles' },
      ]
      this.listGroups.some(function (group) {
        return group.items.some(function (item) {
          if (
            item.to === currentPath ||
            item.to === simplePluralize(currentPath) ||
            groupExceptions.find(
              (o) => o.group === item.to && o.includes === currentPath
            ) ||
            (item.childItems &&
              item.childItems.some(function (ci) {
                if (
                  ci.to === currentPath ||
                  ci.to === simplePluralize(currentPath) ||
                  itemExceptions.find(
                    (o) => o.childItem === ci.to && o.includes === currentPath
                  )
                ) {
                  return true
                }
              }))
          ) {
            group.state = true
            return true
          }
        })
      })
      this.listGroups.forEach((group, index) => {
        console.debug(
          `${fn} AFTER - group: ${group.title}, state: ${group.state}`
        )
      })

      if (this.listGroups.every((group) => !group.state)) {
        console.debug(`${fn} AFTER - all false`)
        console.debug(`${fn} AFTER - setting 'OPERATIONS' group to true`)
        this.listGroups
          .filter((group) => group.title === 'OPERATIONS')
          .forEach((group) => (group.state = true))
        this.listGroups.forEach((group, index) => {
          console.debug(
            `${fn} AFTER - group: ${group.title}, state: ${group.state}`
          )
        })
      }
    },
    async setUserGroups() {
      this.groups = await getGroups()
    },
  },
})
</script>

<style>
/* Give the page title all the width its text needs before the spacer takes any.
   V2's `.v-toolbar__title` was `flex: 0 1 auto` — content-sized, shrinking only
   when there was nothing left — so the title took what it needed and the spacer
   absorbed the remainder. V3's `.v-toolbar-title` is `flex: 1 1 0%` with
   `min-width: 0`, so it and the <v-spacer> SPLIT the free space and long titles
   ellipsised early. Measured on /Opportunities/Overview/Sales/XXO0003 at 1600px:
   Bridge title 833px (fits); here 599px with the inner
   `.v-toolbar-title__placeholder` needing 813px — truncated by 234px — while the
   spacer sat on 579px of it.

   Restoring V2's flex hands the width back. The title still ellipsises when it
   genuinely cannot fit, because flex-shrink stays at 1. */
.page-title-bar-title {
  flex: 0 1 auto;
}

/* ADMIN > "Select Lists" nav flyout: pin to the BOTTOM OF THE WINDOW, not above
   the activator.

   `.v-overlay` is `position: fixed; inset: 0` (viewport-sized) and
   `.v-overlay__content` is absolutely positioned inside it, so `top: auto` +
   `bottom` anchors the menu to the window bottom. The connected location strategy
   still supplies the inline `left`, so the flyout stays aligned to the nav item
   horizontally. `!important` because that strategy writes `top`/`max-height`
   inline on every open.

   `max-height` is re-derived from the viewport rather than from the space above
   the activator, which is what keeps V3's scroll behaviour working on a short
   window: the direct-child `.v-list` gets `overflow: auto; height: 100%` from
   VMenu.css and shrinks to fit, so the list scrolls instead of overflowing.

   Teleported content lives outside this component's subtree, so this rule has to
   be in the unscoped block — a `scoped` style would never reach it. */
.nav-flyout-bottom-pinned {
  top: auto !important;
  bottom: 12px !important;
  max-height: calc(100vh - 24px) !important;
}

/* SELECT LISTS FLYOUT TEXT, dark theme. Same job as the nav-drawer text rules
   further down, but this menu needs its OWN rules: it is teleported to
   `.v-overlay-container`, so `.navDrawerItem` -- a DOM class on the drawer -- can
   never reach it. (Component-tree-wise it is still a drawer descendant, which is
   why VNavigationDrawer's `VList { bgColor: 'transparent' }` default would reach
   it; the explicit `:bg-color` on that list overrides it, so unlike the drawer
   lists there is no `color: currentColor !important` in play here.)
   The selector is a DESCENDANT one because VOverlay puts `themeClasses` on the
   `.v-overlay` root and `contentClass` on the inner `.v-overlay__content` --
   DIFFERENT elements. Don't copy the compound `.v-theme--dark.v-tooltip` form
   used elsewhere for teleported content; that one is correct only because
   tooltips put both classes on the same element.
   These items were rendering PURE WHITE, not the 0.87 the light theme uses:
   dark's `high-emphasis-opacity` is 1 (theme.js), so `.v-list`'s
   `rgba(on-surface, high-emphasis)` resolves to #FFF -- 16.7:1 on this #1E1E1E
   menu.
   #AAAAAA reads 7.2:1 here. ⚠ It is NOT the drawer's #9E9E9E, and the 12-step gap
   is deliberate: this value is pinned by the SELECTED-ROW rule below, not chosen
   on its own. That row's tint was raised to #4B4B4B, and #AAAAAA is what holds the
   text on it at 3.76:1 -- unchanged from what #9E9E9E gave on the older, darker
   bar. Move one and you must re-derive the other. (The two backdrops differ
   anyway -- #1E1E1E here against the drawer's #363636 -- so equal hexes would not
   have read as equal brightness regardless.)
   ⚠ The subheaders have to move WITH the items. They are `.v-list-subheader` at
   `medium-emphasis-opacity` (0.70 in dark) = #BCBCBC, so darkening only the items
   would leave each section LABEL brighter than the items beneath it. #7E7E7E
   keeps the ratio the two had before Vuetify's defaults were overridden
   (188/255 = .737; now 126/170 = .741).
   Neither rule needs `!important`: both are (0,3,0) against Vuetify's (0,1,0). */
.v-theme--dark .nav-flyout-bottom-pinned .v-list-item {
  color: #aaaaaa;
  /* ⚠ This override MUST sit on the item, not on the flyout root -- see the
     selected-row comment below. Verified in-browser: on the root it is silently
     discarded and the row stays at Vuetify's 0.12. */
  --v-activated-opacity: 0.2;
}
.v-theme--dark .nav-flyout-bottom-pinned .v-list-subheader {
  color: #7e7e7e;
}

/* SELECTED-ROW BACKGROUND in that same flyout, dark theme.
   ⚠ V3 paints the selection with `.v-list-item__overlay { background-color:
   currentColor }`, so the row's tint is derived from its TEXT colour. Dimming the
   item text above from #FFF to #9E9E9E therefore also dimmed this: the selected
   row's background fell from rgb(57) to rgb(45) over the rgb(30) menu, roughly
   halving the lift that distinguishes it. Anything that changes list text colour
   in V3 moves the selection tint with it -- check both together.
   Forcing the overlay to white decouples the two, so the tint no longer tracks
   whatever the text is set to. Opacity is raised via `--v-activated-opacity`
   rather than by hard-coding `opacity`, DELIBERATELY: VListItem.css composes the
   hover and focus states additively off that same variable
   (`calc(var(--v-activated-opacity) + var(--v-hover-opacity))`), so overriding the
   variable keeps those states working and correctly spaced, where a fixed opacity
   would freeze the row and kill its hover feedback.
   0.20 over #1E1E1E lands the row on #4B4B4B -- a +45 lift, against +27 before
   the text was dimmed and +15 after it; active+hover resolves to 0.24 -> #545454.
   ⚠ This trades directly against the row's own text contrast, and the two were
   raised TOGETHER: at 0.20 the old #9E9E9E text would have dropped to 3.26:1, so
   the item colour above went to #AAAAAA, holding it at 3.76:1 -- level with the
   3.75:1 the previous, darker bar gave. Raising this further costs roughly 0.5
   per step and requires lightening that text again to compensate; do not move one
   without the other.
   ⚠⚠ WHERE the `--v-activated-opacity` override lives is load-bearing, and this
   is not obvious. EVERY themed Vuetify component stamps `v-theme--dark` on its own
   root, and that class RE-DECLARES THE WHOLE THEME VARIABLE SET -- so a value set
   on an ancestor is wiped by any themed component nested under it. Setting it on
   `.nav-flyout-bottom-pinned` looked right and read back as 0.2 on that element,
   yet the overlay still computed 0.12, because `.v-list.v-theme--dark` and then
   `.v-list-item.v-theme--dark` each re-declared it in between. Measured chain:
     .v-overlay__content.nav-flyout-bottom-pinned  -> 0.2   (our rule)
     .v-list.v-theme--dark                         -> 0.12  (re-declared)
     .v-list-item.v-theme--dark                    -> 0.12  (re-declared)
     span.v-list-item__overlay                     -> 0.12  (inherited)
   It therefore lives on the `.v-list-item` rule above -- the deepest themed
   element containing the overlay -- where our (0,3,0) selector out-ranks
   `.v-theme--dark`'s (0,1,0) ON THAT SAME ELEMENT. Reading the variable back from
   the element you SET it on proves nothing; read it from the element that
   CONSUMES it.
   Multiplier note: the opacity is `calc(var(--v-activated-opacity) *
   var(--v-theme-overlay-multiplier))`, and the multiplier here is the `:root`
   default of 1 -- `bg-<key>` utilities are what override it, and this list is
   given a raw hex, which Vuetify applies as an inline style rather than a class.
   Swapping that `:bg-color` to a theme token would silently double these values. */
.v-theme--dark
  .nav-flyout-bottom-pinned
  .v-list-item--active
  > .v-list-item__overlay {
  background-color: #ffffff;
}

/* Gap between the page-title company avatar and the company name. V2 rendered an
   avatar with `mr-2` (8px) FOLLOWED BY an inter-element whitespace text node that
   painted as a ~6px space in the title font -- a 14px gap total. Vue 3's template
   compiler strips whitespace-only text nodes between elements (condense mode), so
   that ~6px space is gone and only the 8px margin remained, bunching the name
   against the avatar. Restore V2's 14px gap directly on the avatar's right margin
   (replaces the `mr-2` both title avatars carried). Same class on both the
   `OutlinedAvatar` (account) and plain `v-avatar` (all other entity) branches. */
.title-bar-avatar {
  margin-right: 14px;
}
.navDrawerItem .v-select__selection,
.navDrawerItem .v-select__selection--comma,
.navDrawerItem .v-select.v-text-field input {
  color: rgb(75, 75, 75) !important;
}
.v-theme--dark.navDrawerItem .v-select__selection,
.v-theme--dark.navDrawerItem .v-select__selection--comma,
.v-theme--dark.navDrawerItem .v-select.v-text-field input {
  color: rgb(155, 153, 153) !important;
}
.navDrawerItem .navDrawerOverflowXed .groupTitle {
  font-weight: 700 !important;
  width: 115px !important;
  font-size: 11pt !important;
}

.navDrawerItem .groupTitle {
  font-weight: 600 !important;
  width: 115px !important;
}

/* .navDrawerItem .v-list-item__content .v-list-item__title {
  color: rgb(110, 100, 110) !important;
} */

/* NAV ITEM TEXT COLOUR — V2 -> V3 restoration.
   V3's VNavigationDrawer registers `VList { bgColor: 'transparent' }` as a
   component default, which stamps the `bg-transparent` utility on both nav
   lists. That utility carries `color: currentColor !important`, so each list --
   and every item inheriting from it -- takes the drawer's OPAQUE colour instead
   of V3's own `.v-list { color: rgba(on-surface, high-emphasis) }` (0.87). The
   nav text therefore rendered pure black where V2 rendered 0.87. Restore the
   shade. Applied to the list ITEM, not its title, so it flows by inheritance:
   the open group header still wins over it (light via its
   `text-primary-darken-1 !important` purple, dark via the more specific
   `navDrawerListGroupActive` rule below), and the active item still wins via the
   grey below -- a direct colour on the title would override BOTH instead. */
.navDrawerItem .v-theme--light.v-list-item {
  color: rgba(0, 0, 0, 0.87);
}
/* Dark nav text. V2 rendered these at pure white (measured) — this is a
   deliberate step AWAY from that parity, not a drift from it: full white on the
   #363636 drawer read as glaring, so it is dimmed to Vuetify's grey base.
   4.51:1 on the #363636 drawer, so it still clears WCAG AA for body text —
   that is the floor here, and this value sits just above it. Light mode is a
   SEPARATE rule (0.87 black, still V2-accurate) and is intentionally unchanged.
   ⚠ This shade and the ACTIVE one below are tuned as ONE value; re-pick them
   together. Both themes use the same relationship: the current-page item is
   pulled toward mid-grey and reads DIMMER than the base. Measured, that is
   14.2:1 -> 3.8:1 in light and 4.5:1 -> 3.5:1 here.
   The dark spread is much tighter, and unavoidably so: light's base is near-black
   with enormous headroom to recede from, whereas this one is already close to the
   AA floor (going under it lands ~2.6:1). A proportional mirror is therefore not
   available — matching light's 3.7x drop would put the active item at 1.2:1,
   invisible. What is mirrored is the DIRECTION plus light's absolute active
   ratio, which is why the two rows sit only ~20 steps apart and the active row
   leans partly on its highlight bar. If that separation ever needs to be wider,
   the lever is to RAISE this base shade (more headroom), not to lower the active
   one. An earlier pass tried inverting dark so the active row was the brightest;
   it read as too severe and was reverted — the two themes match on purpose. */
.navDrawerItem .v-theme--dark.v-list-item {
  color: #9e9e9e;
}

/* Active (current-page) item text = grey. V2 set this via `:color=
   "navDrawerListGroupColor"` on <v-list-group> (light rgb(120,120,120) / dark
   Vuetify grey); that binding was dropped in the migration, so the selected
   item rendered the same near-black as the rest and stopped standing out. The
   extra `.v-list-item--active` class (0,3,0) beats the default rule above
   (0,2,0). `.v-list-item--active` here is set solely by our activeNavItemTo class
   binding (the items carry `:active="false"`, so Vuetify never adds it).
   ⚠ HOME is the exception, via `homeNavItemActiveClass` on that same binding.
   Vuetify `text-*` utilities carry `!important`, so any utility bound there beats
   these rules regardless of specificity. DARK deliberately binds none, letting
   the dark rule below govern HOME like every other item — required anyway, since
   #8A8A8A sits between two Vuetify grey steps. LIGHT still binds `text-grey`
   (#9E9E9E), so the rgb(120,120,120) here has never actually applied to HOME.
   That mismatch is pre-existing and was left alone: this round was dark-only.
   The group HEADER is highlighted through `navDrawerListGroupActiveClass`, not
   `v-list-item--active`, so it stays purple and is untouched here.
   Both rules below sit DIMMER than their theme's base shade — the same
   recede-when-active treatment in each. See the ⚠ note on the base rule for why
   dark's spread is necessarily the tighter of the two. */
.navDrawerItem .v-theme--light.v-list-item.v-list-item--active {
  color: rgb(120, 120, 120);
}
.navDrawerItem .v-theme--dark.v-list-item.v-list-item--active {
  color: #8a8a8a;
}

/* Open (expanded) group header, dark theme -- the teal one. Bound by
   `navDrawerListGroupActiveClass`; light mode stays on the `text-primary-darken-1`
   utility and never reaches this rule.
   #48BFB0 is secondary (#009688, teal base) shifted +15 in CIELAB lightness --
   deliberately HALF a variation step, between lighten-1 (+10, #37B1A2) and
   lighten-2 (+20, #58CDBD). Derived with Vuetify's own algorithm; validated by
   reproducing the documented secondary-darken-1 = #007c6f anchor exactly. To
   re-derive: sRGB -> XYZ -> CIELAB (D65), L += 15, back.
   ⚠ Because it is a half step it CANNOT be written as a `text-secondary-lighten-N`
   utility -- that is why this rule exists at all rather than a class name in the
   computed. If you ever move it back onto a whole step, delete this rule and
   return the utility instead.
   No `!important` needed, unlike the utility it replaced: this is (0,4,0) and
   beats the (0,3,0) base nav-text rule above. It cannot clash with the
   `.v-list-item--active` rule at the same weight because the group header has no
   `to` and carries a static `isOpen`-driven class only, so Vuetify never marks it
   active -- the two selectors can never both match one element. */
.navDrawerItem .v-theme--dark.v-list-item.navDrawerListGroupActive {
  color: #48bfb0;
}
/* The `opacity: 1` on each of these four is a V2 -> V3 restoration, not a new
   design choice. V2's `.v-divider` had no opacity, so the alpha these rules ask
   for was the alpha you saw. V3 fades every divider with
   `.v-divider { opacity: var(--v-border-opacity) }` (0.12), which MULTIPLIES the
   border-color alpha: the light-theme section divider rendered at 0.3 * 0.12 =
   ~0.036 instead of 0.3, i.e. nearly invisible. Restoring opacity makes the
   declared alpha the effective one again, exactly as in V2. (Measured: with the
   fade in place, border-top-color computed IDENTICALLY in both apps -- only
   opacity differed, 1 vs 0.12.) Same fix, same reason, as the `.v-divider--thick`
   rule in SupplierProductItemsMenu.vue. */
.navDrawerItem .v-theme--dark.v-divider--inset {
  border-color: rgba(252, 251, 251, 0.25) !important;
  opacity: 1;
}
.navDrawerItem .v-theme--dark.v-divider {
  border-color: rgba(252, 251, 251, 0.15) !important;
  opacity: 1;
}
.navDrawerItem .v-theme--light.v-divider--inset {
  border-color: rgba(100, 100, 100, 0.4) !important;
  opacity: 1;
}
.navDrawerItem .v-theme--light.v-divider {
  border-color: rgba(100, 100, 100, 0.3) !important;
  opacity: 1;
}

/* Active nav-item highlight bar. V3's active list-item overlay tints with the
   item's currentColor -- black here -- for a cooler/darker bar than V2, which
   used a fixed rgb(120,120,120) grey at the same 0.12 active opacity. Restore
   V2's grey on the light-theme nav. Scoped to `.v-theme--light` so dark keeps
   V3's light-on-dark overlay (which already reads like V2's). */
.v-theme--light.navDrawerItem .v-list-item--active .v-list-item__overlay {
  background-color: rgb(120, 120, 120);
}

/* Bullet-nosed active/hover highlight, restoring V2's `<v-list shaped>` look.
   V3 dropped the shaped v-list style (the `shaped` prop is inert), so the
   active-item overlay rendered as a full-width rectangle. V2 rounded each item's
   trailing (right) end -- `border-radius: 0 32px 32px 0` -- with an 8px gap to
   the drawer edge (list `padding-right: 8px`). The overlay inherits the item's
   radius (VListItem.css `.v-list-item__overlay { border-radius: inherit }`), so
   rounding the item bullet-noses its highlight. `!important` is required: V3's
   VListItem computes `rounded = props.rounded || props.nav`, which is `false`
   for these non-nav items, emitting a `rounded-0` class (`border-radius:0
   !important`); our (0,2,0) selector + `!important` out-ranks that (0,1,0). We
   don't add the `nav` prop instead because `.v-list-item--nav` also restyles the
   item's title/subtitle sizing. */
.navDrawerItem .v-list {
  padding-right: 8px;
}
.navDrawerItem .v-list-item {
  border-radius: 0 32px 32px 0 !important;
}

/* Vuetify 3 indents v-list-group children by the parent's prepend width plus a
   step, pushing the nav subitems ~56px right of their parent group. Zero the
   indent so subitem bullets align under the parent icons and subitem titles align
   under the parent titles. The child padding rule is !important but reads this var. */
.v-navigation-drawer .v-list-group__items {
  --indent-padding: 0px;
}

/* PAGE-LOAD PROGRESS BAR colour (see <NuxtLoadingIndicator> at the top of the
   template). Theme-aware, because a single colour cannot work in both: the bar
   overlays the app bar (#F5F5F5 light / #272727 dark) and the nav drawer
   (#EEEEEE / #363636), and both themes set the SAME `primary`, so a fixed
   token that reads in one is invisible in the other. primary-darken-3 measured
   1.52:1 against the light chrome but 1.03:1 against the dark -- gone.
   Dark is a SOLID primary-lighten-1, chosen by eye from a side-by-side render
   of candidates in the running app. It used to be primary-lighten-4 at light
   mode's 0.2 alpha, picked because its contrast ratio matched light mode's
   1.52:1 almost exactly (1.54 app bar / 1.51 drawer) -- and it was reported as
   not visible at all. A 4px band of translucent mauve on near-black reads as
   noise whatever the ratio says, so don't re-tune this by matching ratios.
   Translucent lighten-4 stayed a muddy grey up to 0.5 alpha; lighten-2 and up
   solid, or lighten-4 near-opaque, read as neon pink (lighten-4 is
   (255,154,255), clipped at both ends, so nearer magenta than the base purple).
   lighten-1 is the first step that is plainly visible and still purple.
   These tokens exist only because nuxt.config.ts sets theme.variations
   (lighten: 5, darken: 4); without that block the var resolves to nothing and
   the bar renders transparent.
   Switched on Vuetify's own theme class rather than a JS accessor, so it
   follows a theme toggle with no reactivity to maintain. The custom property is
   declared on the `.v-theme--*` root and inherits down to the indicator. */
.v-theme--light {
  --cynque-loading-bar: rgba(var(--v-theme-primary-darken-3), 0.2);
}
.v-theme--dark {
  --cynque-loading-bar: rgb(var(--v-theme-primary-lighten-1));
}
</style>
